import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Check, Trash2 } from "lucide-react";

/* ============================================================
   MILES OPERATIONS — Design System
   Hiérarchie : TIME → JOURNEY → STATUS → ACTION
   65 % White/Cloud · 20 % Ice Blue · 10 % Royal Blue · 5 % états
   ============================================================ */

// ---------- Tokens ----------

export const T = {
  pageTitle: "text-[28px] font-semibold tracking-tight text-ink",
  sectionTitle: "text-[17px] font-semibold tracking-tight text-ink",
  primary: "text-[15px] text-ink",
  secondary: "text-[13px] text-slate",
  meta: "font-mono text-[11px] tracking-wide text-slate/80",
  card: "rounded-2xl border border-ink/[0.06] bg-white shadow-[0_1px_2px_rgba(16,24,40,0.04)]",
  input: "h-11 w-full rounded-xl border border-ink/10 bg-white px-3.5 text-[15px] text-ink placeholder:text-slate/50 outline-none transition-all duration-150 focus:border-miles focus:ring-4 focus:ring-miles/10",
  select: "h-11 w-full rounded-xl border border-ink/10 bg-white px-3.5 text-[15px] text-ink outline-none transition-all duration-150 focus:border-miles focus:ring-4 focus:ring-miles/10",
  // alias boutons (utilisés comme classes sur <Link>)
  btnPrimary: "inline-flex h-10 items-center justify-center gap-2 rounded-xl bg-miles px-4 text-sm font-semibold text-white shadow-[0_4px_14px_rgba(36,87,255,0.25)] transition-all duration-150 hover:bg-miles-dark active:scale-[0.98]",
  btnSecondary: "inline-flex h-10 items-center justify-center gap-2 rounded-xl border border-ink/10 bg-white px-4 text-sm font-semibold text-ink transition-all duration-150 hover:border-miles/35 hover:text-miles active:scale-[0.98]",
};

// ---------- Boutons ----------

const BTN_BASE = "inline-flex h-10 items-center justify-center gap-2 rounded-xl px-4 text-sm font-semibold transition-all duration-150 active:scale-[0.98] disabled:pointer-events-none disabled:opacity-45";
const BTN_VARIANTS = {
  primary: "bg-miles text-white shadow-[0_4px_14px_rgba(36,87,255,0.25)] hover:bg-miles-dark",
  secondary: "border border-ink/10 bg-white text-ink hover:border-miles/35 hover:text-miles",
  ghost: "text-slate hover:bg-ink/[0.05] hover:text-ink",
  danger: "border border-red-200 bg-white text-red-600 hover:bg-red-50",
};

export function Btn({ variant = "primary", className = "", ...props }) {
  return <button type="button" className={`${BTN_BASE} ${BTN_VARIANTS[variant]} ${className}`} {...props} />;
}

// compatibilité avec les anciens imports
export const inputCls = T.input;
export const btnPrimary = `${BTN_BASE} ${BTN_VARIANTS.primary}`;
export const btnSecondary = `${BTN_BASE} ${BTN_VARIANTS.secondary}`;
export const btnDanger = `${BTN_BASE} ${BTN_VARIANTS.danger}`;

// ---------- Système de statuts MILES (couche humaine) ----------

const STATUS = {
  // cycle réservation
  pending_payment: ["Paiement en attente", "amber"],
  payment_failed: ["Paiement échoué", "red"],
  booked: ["Confirmée", "blue"],
  driver_assigned: ["Chauffeur attribué", "blue"],
  driver_on_the_way: ["Chauffeur en route", "cyan"],
  driver_arrived: ["Chauffeur arrivé", "cyan"],
  on_board: ["Passager à bord", "cyan"],
  completed: ["Terminée", "green"],
  cancelled: ["Annulée", "neutral"],
  // paiements
  initiated: ["Initié", "neutral"],
  pending: ["En attente", "amber"],
  succeeded: ["Réussi", "green"],
  failed: ["Échoué", "red"],
  paid: ["Payé", "green"],
  refunded: ["Remboursé", "red"],
  partially_refunded: ["Partiellement remboursé", "amber"],
  manual: ["Hors plateforme", "neutral"],
  invoiced: ["Facturé", "blue"],
  // devis & factures
  draft: ["Brouillon", "neutral"],
  sent: ["Envoyé", "blue"],
  viewed: ["Consulté", "blue"],
  accepted: ["Accepté", "green"],
  declined: ["Décliné", "red"],
  rejected: ["Refusé", "red"],
  expired: ["Expiré", "neutral"],
  converted: ["Converti", "green"],
  issued: ["Émise", "blue"],
  credited: ["Avoir émis", "neutral"],
  // factures
  partially_paid: ["Partiellement payée", "amber"],
  overdue: ["En retard", "red"],
  // chauffeurs
  available: ["Disponible", "green"],
  active: ["Actif", "green"],
  busy: ["Occupé", "amber"],
  offline: ["Hors service", "neutral"],
  suspended: ["Suspendu", "red"],
  // véhicules
  on_trip: ["En trajet", "cyan"],
  unavailable: ["Indisponible", "neutral"],
  maintenance: ["Maintenance", "amber"],
};

const STATUS_TONE = {
  blue: "bg-miles-ice text-miles",
  cyan: "bg-sky-50 text-sky-700",
  green: "bg-emerald-50 text-emerald-700",
  amber: "bg-amber-50 text-amber-700",
  red: "bg-red-50 text-red-600",
  neutral: "bg-ink/[0.05] text-slate",
};
const DOT_TONE = {
  blue: "bg-miles", cyan: "bg-sky-500", green: "bg-emerald-500",
  amber: "bg-amber-500", red: "bg-red-500", neutral: "bg-slate/50",
};

export const STATUS_LABELS = Object.fromEntries(Object.entries(STATUS).map(([k, v]) => [k, v[0]]));

export function StatusBadge({ status, testId }) {
  const [label, tone] = STATUS[status] || [status, "neutral"];
  return (
    <span data-testid={testId}
      className={`inline-flex items-center gap-1.5 rounded-full px-2.5 py-1 text-xs font-medium ${STATUS_TONE[tone]}`}>
      <span className={`h-1.5 w-1.5 rounded-full ${DOT_TONE[tone]}`} />
      {label}
    </span>
  );
}

// ---------- MILES Route Line ----------

export function RouteLine({ pickup, dropoff, compact = false, testId, dark = false }) {
  if (compact) {
    return (
      <span className="flex min-w-0 items-center gap-2 text-sm" data-testid={testId}>
        <span className={`h-1.5 w-1.5 shrink-0 rounded-full ${dark ? "bg-miles-sky" : "bg-miles"}`} />
        <span className={`min-w-0 flex-1 truncate font-medium ${dark ? "text-white" : "text-ink"}`}>{pickup}</span>
        <span className={`shrink-0 ${dark ? "text-white/40" : "text-slate/50"}`}>→</span>
        <span className={`min-w-0 flex-1 truncate ${dark ? "text-white/90" : "text-ink"}`}>{dropoff || "à l'heure"}</span>
      </span>
    );
  }
  return (
    <div className="flex gap-3.5" data-testid={testId}>
      <div className="flex flex-col items-center pt-1.5">
        <span className="h-2.5 w-2.5 rounded-full border-[3px] border-miles bg-white" />
        <span className="my-1 w-px flex-1 bg-gradient-to-b from-miles/60 to-miles-deep/40" />
        <span className="h-2.5 w-2.5 rounded-full bg-miles-deep" />
      </div>
      <div className="flex flex-col justify-between gap-4 py-0.5">
        <p className="text-[15px] font-medium leading-snug text-ink">{pickup}</p>
        <p className="text-[15px] font-medium leading-snug text-ink">{dropoff || "Mise à disposition"}</p>
      </div>
    </div>
  );
}

// ---------- Journey Stepper ----------

const JOURNEY_STEPS = [
  ["booked", "Confirmée"], ["driver_assigned", "Chauffeur attribué"],
  ["driver_on_the_way", "En route"], ["driver_arrived", "Arrivé"],
  ["on_board", "À bord"], ["completed", "Terminée"],
];

export function JourneyStepper({ current, onSelect, disabled, testId }) {
  const currentIdx = JOURNEY_STEPS.findIndex(([s]) => s === current);
  return (
    <ol className="flex flex-col gap-0" data-testid={testId}>
      {JOURNEY_STEPS.map(([status, label], i) => {
        const done = i < currentIdx;
        const active = i === currentIdx;
        return (
          <li key={status} className="flex items-center gap-3">
            <div className="flex flex-col items-center">
              <button type="button" disabled={disabled} onClick={() => onSelect?.(status)}
                data-testid={`step-${status}`} aria-label={label}
                className={`flex h-6 w-6 items-center justify-center rounded-full border-2 transition-all duration-150 ${
                  done ? "border-miles bg-miles text-white"
                  : active ? "border-miles bg-white" : "border-ink/15 bg-white hover:border-miles/40"}`}>
                {done ? <Check className="h-3 w-3" strokeWidth={3} />
                  : active ? <span className="h-2 w-2 rounded-full bg-miles" /> : null}
              </button>
              {i < JOURNEY_STEPS.length - 1 && (
                <span className={`my-0.5 h-4 w-0.5 rounded ${i < currentIdx ? "bg-miles" : "bg-ink/10"}`} />
              )}
            </div>
            <span className={`text-sm ${active ? "font-semibold text-ink" : done ? "text-slate" : "text-slate/60"}`}>{label}</span>
          </li>
        );
      })}
    </ol>
  );
}

// ---------- En-tête de page ----------

export function PageHeader({ title, subtitle, actions, testId }) {
  return (
    <header className="mb-6 flex flex-wrap items-end justify-between gap-4" data-testid={testId}>
      <div>
        <h1 className={T.pageTitle}>{title}</h1>
        {subtitle && <p className={`${T.secondary} mt-1`}>{subtitle}</p>}
      </div>
      {actions && <div className="flex items-center gap-2.5">{actions}</div>}
    </header>
  );
}

// ---------- Tableau de données ----------

export function Table({ head, children, testId }) {
  return (
    <div className="overflow-x-auto" data-testid={testId}>
      <table className="w-full min-w-[640px] text-left text-sm [&_td]:px-5 [&_td]:py-3.5 [&_tbody_tr]:border-t [&_tbody_tr]:border-ink/[0.05]">
        <thead>
          <tr className="[&_th]:px-5 [&_th]:pb-3 [&_th]:pt-1">
            {head.map((h, i) => (
              <th key={i} className="text-[11px] font-semibold uppercase tracking-[0.12em] text-slate/60">{h}</th>
            ))}
          </tr>
        </thead>
        <tbody>{children}</tbody>
      </table>
    </div>
  );
}

// ---------- Liste mobile (cartes tactiles) ----------
// Desktop garde ses tableaux ; mobile reçoit des cartes pleine largeur.
export function MobileCards({ children, testId }) {
  return <div className="space-y-2.5 p-4 md:hidden" data-testid={testId}>{children}</div>;
}

// ---------- Surfaces ----------

export function Panel({ title, action, children, testId, className = "", padded = true }) {
  return (
    <section data-testid={testId} className={`${T.card} ${className}`}>
      {(title || action) && (
        <header className="flex flex-wrap items-center justify-between gap-2 px-5 pt-4">
          <h2 className={T.sectionTitle}>{title}</h2>
          {action}
        </header>
      )}
      <div className={padded ? "p-5" : ""}>{children}</div>
    </section>
  );
}

export function KvRow({ label, children, mono = false }) {
  return (
    <div className="flex items-start justify-between gap-4 py-2 text-sm">
      <span className="text-slate">{label}</span>
      <span className={`text-right font-medium text-ink ${mono ? "font-mono text-[13px]" : ""}`}>{children ?? "—"}</span>
    </div>
  );
}

export function Avatar({ firstName = "", lastName = "", size = "md" }) {
  const initials = `${firstName[0] || ""}${lastName[0] || ""}`.toUpperCase() || "?";
  const sizes = { sm: "h-7 w-7 text-[10px]", md: "h-9 w-9 text-xs", lg: "h-11 w-11 text-sm" };
  return (
    <span className={`flex shrink-0 items-center justify-center rounded-full bg-miles-ice font-semibold text-miles ${sizes[size]}`}>
      {initials}
    </span>
  );
}

export function EmptyState({ title, hint, action, testId }) {
  return (
    <div className="flex flex-col items-center gap-1.5 py-10 text-center" data-testid={testId}>
      <p className="text-sm font-medium text-ink">{title}</p>
      {hint && <p className="text-[13px] text-slate">{hint}</p>}
      {action && <div className="mt-2">{action}</div>}
    </div>
  );
}

export function Skeleton({ rows = 4 }) {
  return (
    <div className="space-y-2.5" data-testid="skeleton">
      {Array.from({ length: rows }).map((_, i) => (
        <div key={i} className="h-11 animate-pulse rounded-xl bg-ink/[0.05]" style={{ animationDelay: `${i * 60}ms` }} />
      ))}
    </div>
  );
}

export function Drawer({ title, onClose, children, testId }) {
  useEffect(() => {
    const onKey = (e) => e.key === "Escape" && onClose();
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose]);
  return (
    <div className="fixed inset-0 z-50" data-testid={testId}>
      <div className="absolute inset-0 bg-ink/40 backdrop-blur-[2px] transition-opacity" onClick={onClose} />
      <div className="absolute inset-y-0 right-0 w-full max-w-md overflow-auto border-l border-ink/[0.06] bg-cloud p-6 shadow-[-8px_0_30px_rgba(16,24,40,0.08)]">
        <div className="mb-5 flex items-center justify-between">
          <h2 className={T.sectionTitle}>{title}</h2>
          <Btn variant="ghost" onClick={onClose} className="h-8 w-8 px-0" aria-label="Fermer">✕</Btn>
        </div>
        {children}
      </div>
    </div>
  );
}

// ---------- Humanisation des événements ----------

const ACTIVITY_PHRASES = {
  "payment.succeeded": ["Paiement confirmé", (e) => e.meta?.amount_chf ? `${e.meta.amount_chf} CHF` : null],
  "booking.confirmed": ["Réservation confirmée", () => null],
  "booking.created": ["Nouvelle réservation Business", (e) => e.message?.split(" par ")[1] ? `Créée par ${e.message.split(" par ")[1]}` : null],
  "booking.created_manual": ["Réservation créée manuellement", () => null],
  "booking.updated": ["Réservation modifiée", () => null],
  "booking.duplicated": ["Réservation dupliquée", () => null],
  "booking.cancelled": ["Réservation annulée", () => null],
  "booking.confirmation_resent": ["Confirmation renvoyée au client", () => null],
  "booking.status.driver_assigned": ["Chauffeur attribué", () => null],
  "booking.status.driver_on_the_way": ["Chauffeur en route", () => null],
  "booking.status.driver_arrived": ["Chauffeur arrivé", () => null],
  "booking.status.on_board": ["Passager à bord", () => null],
  "booking.status.completed": ["Trajet terminé", () => null],
  "driver.assigned": ["Chauffeur attribué", (e) => e.message?.includes("attribué") ? e.message.split(" attribué")[0].replace("Chauffeur ", "") : null],
  "payment.refunded": ["Remboursement émis", (e) => e.meta?.amount_chf ? `${e.meta.amount_chf} CHF remboursés` : null],
  "trip.completed": ["Trajet terminé", () => null],
  "invoice.created": ["Facture générée", () => null],
  "invoice.updated": ["Facture mise à jour", () => null],
  "quote.created": ["Devis créé", () => null],
  "quote.converted": ["Devis converti en réservation", () => null],
  "incident.created": ["Incident déclaré", () => null],
  "customer.updated": ["Fiche client mise à jour", () => null],
  "driver.created": ["Chauffeur ajouté", () => null],
  "driver.updated": ["Fiche chauffeur mise à jour", () => null],
  "vehicle.created": ["Véhicule ajouté", () => null],
  "payout.created": ["Payout chauffeur créé", () => null],
};

// événements purement techniques → audit log, pas le fil opérationnel
const AUDIT_ONLY = ["admin.login", "settings.updated", "quote.sent", "quote.viewed", "quote.declined", "quote.accepted", "quote.expired"];

export function isAuditEvent(e) {
  return AUDIT_ONLY.includes(e.type);
}

export function humanActivity(e) {
  const [title, detailFn] = ACTIVITY_PHRASES[e.type] || [e.message, () => null];
  const who = e.actor?.startsWith("admin:") ? e.actor.slice(6).split("@")[0]
    : e.actor === "driver" ? "Chauffeur"
    : e.actor === "tracking" ? "Suivi client"
    : e.actor?.startsWith("business:") ? e.actor.slice(9).split("@")[0] : null;
  return { title, detail: detailFn(e), who };
}

export const COMM_LABELS = {
  booking_confirmed: "Confirmation de réservation",
  guest_ride: "Invitation passager Business",
  status_driver_assigned: "Chauffeur attribué",
  status_driver_on_the_way: "Chauffeur en route",
  status_driver_arrived: "Chauffeur arrivé",
};

// ---------- Formatage ----------

export function money(value) {
  const n = Number(value || 0);
  return `CHF ${n % 1 === 0 ? n.toLocaleString("fr-CH") : n.toLocaleString("fr-CH", { minimumFractionDigits: 2 })}`;
}

export function shortDate(iso) {
  if (!iso) return "—";
  return iso.slice(0, 10).split("-").reverse().join(".");
}

export function timeAgo(iso) {
  if (!iso) return "—";
  const diff = Date.now() - new Date(iso).getTime();
  const min = Math.floor(diff / 60000);
  if (min < 1) return "à l'instant";
  if (min < 60) return `il y a ${min} min`;
  const h = Math.floor(min / 60);
  if (h < 24) return `il y a ${h} h`;
  return `il y a ${Math.floor(h / 24)} j`;
}

export function BookingLink({ code, className = "" }) {
  return (
    <Link to={`/admin/app/bookings/${code}`}
      className={`font-mono text-[13px] font-semibold text-miles hover:underline ${className}`}>
      {code}
    </Link>
  );
}

// Suppression en deux temps : « Supprimer » → « Confirmer ? » (4 s pour annuler).
// Affiche l'erreur API (ex. garde-fou 409) au lieu d'échouer en silence.
export function DeleteButton({ onConfirm, onDeleted, testId, label = "Supprimer", small = false, className = "" }) {
  const [armed, setArmed] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    if (!armed) return undefined;
    const timer = setTimeout(() => setArmed(false), 4000);
    return () => clearTimeout(timer);
  }, [armed]);

  const run = async (event) => {
    event.stopPropagation();
    if (!armed) { setArmed(true); setError(""); return; }
    setBusy(true);
    try {
      await onConfirm();
      setArmed(false);
      onDeleted?.();
    } catch (e) {
      setError(e?.response?.data?.detail || "Suppression impossible");
      setArmed(false);
    } finally {
      setBusy(false);
    }
  };

  return (
    <span className={`inline-flex flex-col items-start gap-1 ${className}`} onClick={(e) => e.stopPropagation()}>
      <button type="button" onClick={run} disabled={busy} data-testid={testId}
        className={`inline-flex items-center gap-1.5 rounded-lg font-semibold transition-colors disabled:opacity-50 ${
          small ? "h-8 px-2.5 text-xs" : "h-10 px-4 text-sm"
        } ${armed ? "bg-red-600 text-white hover:bg-red-700" : "border border-red-200 bg-white text-red-600 hover:border-red-400"}`}>
        <Trash2 className={small ? "h-3.5 w-3.5" : "h-4 w-4"} strokeWidth={2} />
        {busy ? "…" : armed ? "Confirmer ?" : label}
      </button>
      {error && <span className="max-w-[260px] text-xs font-medium text-red-600" data-testid={`${testId}-error`}>{error}</span>}
    </span>
  );
}
